import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, Trash2, AlertTriangle, X, Sparkles, Clock, FileSearch, Scale, Zap } from 'lucide-react';
import { api } from '../utils/apiClient';
const initialAlerts = [
  { id: 1, icon: 'factura', text: 'Factura FC-003-2024 vencida - Suramericana', time: 'Hace 2 horas' },
  { id: 2, icon: 'documento', text: 'Caso LP-2024-002: requiere documentos', time: 'Hace 30 min' },
];

const SUGERENCIAS = [
  { icon: FileSearch, texto: 'Busca expediente 10503912' },
  { icon: Scale, texto: 'Últimos casos legales' },
  { icon: Clock, texto: 'Casos sin gestionar' },
];

const BIENVENIDA = {
  role: 'assistant',
  content: '¡Hola! Soy **Omnia**, tu asistente jurídico inteligente de LegalPlus.\n\nPuedo consultar expedientes, casos y asistencias en tiempo real directamente desde la base de datos.\n\nPrueba con alguna de las sugerencias o escribe tu consulta.',
};

function MensajeBot({ texto }) {
  const lineas = texto.split('\n');
  return (
    <div className="space-y-1">
      {lineas.map((linea, i) => {
        if (!linea.trim()) return <div key={i} className="h-1" />;
        const partes = linea.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
        return (
          <p key={i} className="leading-relaxed">
            {partes.map((parte, j) => {
              if (parte.startsWith('**') && parte.endsWith('**'))
                return <strong key={j} className="text-white font-semibold">{parte.slice(2, -2)}</strong>;
              if (parte.startsWith('*') && parte.endsWith('*'))
                return <em key={j} className="text-blue-300 not-italic">{parte.slice(1, -1)}</em>;
              if (parte.startsWith('- '))
                return <span key={j} className="block pl-2 before:content-['•'] before:mr-2 before:text-blue-400">{parte.slice(2)}</span>;
              return parte;
            })}
          </p>
        );
      })}
    </div>
  );
}

const IAAsistente = () => {
  const [mensajes, setMensajes] = useState([BIENVENIDA]);
  const [input, setInput] = useState('');
  const [cargando, setCargando] = useState(false);
  const [alerts, setAlerts] = useState(initialAlerts);
  const [consultasHoy, setConsultasHoy] = useState(0);
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [mensajes, cargando]);

  const handleEnviar = async (textoOverride) => {
    const texto = (textoOverride ?? input).trim();
    if (!texto || cargando) return;

    const nuevosMensajes = [...mensajes, { role: 'user', content: texto }];
    setMensajes(nuevosMensajes);
    setInput('');
    setCargando(true);

    const SYSTEM_PROMPT = `Eres Omnia, el asistente jurídico inteligente de LegalPlus Partners.
Tu misión es responder consultas del equipo legal de forma clara, precisa y conversacional en español.
Se te proporcionarán datos reales de la base de datos. Usa esos datos para responder.
Si los datos están vacíos, dilo claramente y sugiere cómo refinar la búsqueda.
No inventes información. Sé conciso y profesional.`;

    try {
      const historial = nuevosMensajes
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      // 1. Consultar BD en el backend (sin costo de IA)
      let contextoDb = '';
      try {
        const ctxData = await api.post('/omnia_contexto', { mensaje: texto });
        if (ctxData.datos && ctxData.datos.length > 0) {
          const datosStr = JSON.stringify(ctxData.datos, null, 2);
          contextoDb = `\n\n[DATOS DE LA BASE DE DATOS — ${ctxData.descripcion}]\n${datosStr}\n[FIN DE DATOS]`;
        }
      } catch (_) {
        // Si el backend falla, continuar sin contexto de BD
      }

      // 2. Construir mensajes y enviar al backend (API key segura en servidor)
      const mensajeConContexto = `${SYSTEM_PROMPT}\n\n---\n\n${texto}${contextoDb}`;
      const historialPrevio = historial.slice(0, -1);
      while (historialPrevio.length > 0 && historialPrevio[0].role === 'assistant') {
        historialPrevio.shift();
      }
      const mensajesIA = [
        ...historialPrevio,
        { role: 'user', content: mensajeConContexto },
      ];

      // Llamar al proxy del backend (la API key nunca sale del servidor)
      const resultado = await api.post('/omnia_chat', { messages: mensajesIA });

      if (resultado.respuesta) {
        setMensajes(prev => [...prev, { role: 'assistant', content: resultado.respuesta }]);
        setConsultasHoy(n => n + 1);
      } else {
        throw new Error(resultado.error || 'Todos los modelos estan saturados, intenta en un momento.');
      }
    } catch (err) {
      const msg = err.message || 'Error de conexión';
      setMensajes(prev => [...prev, { role: 'assistant', content: `⚠️ ${msg}` }]);
    } finally {
      setCargando(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleLimpiar = () => {
    setMensajes([BIENVENIDA]);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">

      {/* ── HEADER BANNER ── */}
      <div className="relative overflow-hidden px-6 py-5 border-b border-slate-700/50 bg-gradient-to-r from-blue-950/60 via-slate-900 to-violet-950/40 flex-shrink-0">
        {/* Orbs decorativos */}
        <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-600/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -top-4 right-20 w-24 h-24 bg-violet-600/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar animado */}
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-xl shadow-blue-900/40">
                <Bot size={24} className="text-white" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-slate-900 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-extrabold text-white tracking-tight">Omnia</h1>
                <span className="flex items-center gap-1 text-xs font-medium bg-violet-900/50 text-violet-300 border border-violet-700/50 px-2 py-0.5 rounded-full">
                  <Sparkles size={10} />
                  IA Jurídica
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Consultas en tiempo real · Base de datos activa</p>
            </div>
          </div>

          {/* Stats rápidas */}
          <div className="hidden sm:flex items-center gap-4">
            <div className="text-center">
              <p className="text-lg font-extrabold text-white">{consultasHoy}</p>
              <p className="text-xs text-slate-500">Consultas</p>
            </div>
            <div className="w-px h-8 bg-slate-700" />
            <div className="text-center">
              <p className="text-lg font-extrabold text-emerald-400">●</p>
              <p className="text-xs text-slate-500">Online</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── CUERPO: chat + alertas ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── PANEL CHAT (izquierda) ── */}
        <div className="flex flex-col flex-1 min-w-0">

          {/* Mensajes */}
          <div ref={chatRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-5 custom-scrollbar">
            {mensajes.map((msg, i) => (
              <div key={i} className={`flex items-end gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>

                {/* Avatar bot */}
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-blue-900/30">
                    <Bot size={15} className="text-white" />
                  </div>
                )}

                {/* Burbuja */}
                <div className={`group relative max-w-[78%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col`}>
                  <div className={`px-4 py-3 text-sm shadow-lg ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl rounded-br-sm shadow-blue-900/30'
                      : 'bg-slate-800/90 border border-slate-700/60 text-slate-200 rounded-2xl rounded-bl-sm backdrop-blur-sm'
                  }`}>
                    {msg.role === 'assistant'
                      ? <MensajeBot texto={msg.content} />
                      : <p className="leading-relaxed">{msg.content}</p>
                    }
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {cargando && (
              <div className="flex items-end gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-blue-900/30">
                  <Bot size={15} className="text-white" />
                </div>
                <div className="bg-slate-800/90 border border-slate-700/60 backdrop-blur-sm rounded-2xl rounded-bl-sm px-4 py-3.5">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '160ms' }} />
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '320ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sugerencias rápidas */}
          {mensajes.length <= 1 && !cargando && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGERENCIAS.map(({ icon: Icon, texto }) => (
                <button
                  key={texto}
                  onClick={() => handleEnviar(texto)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-slate-800/70 border border-slate-700/60 text-slate-300 hover:bg-slate-700/80 hover:text-white hover:border-blue-600/50 transition-all duration-200"
                >
                  <Icon size={12} className="text-blue-400" />
                  {texto}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-4 pb-4 pt-2 border-t border-slate-700/50 bg-slate-900/60 backdrop-blur-sm flex-shrink-0">
            <div className="flex items-center gap-2 bg-slate-800/80 border border-slate-700/60 rounded-2xl px-3 py-1.5 focus-within:border-blue-500/60 focus-within:ring-1 focus-within:ring-blue-500/20 transition-all duration-200">
              <button
                onClick={handleLimpiar}
                title="Nueva conversación"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-700/60 transition-colors duration-200 flex-shrink-0"
              >
                <Trash2 size={15} />
              </button>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleEnviar(); }}
                disabled={cargando}
                placeholder="Escribe tu consulta… ej: expediente 10503912"
                className="flex-1 bg-transparent text-slate-100 placeholder:text-slate-500 text-sm py-1.5 outline-none disabled:opacity-50"
              />
              <button
                onClick={() => handleEnviar()}
                disabled={cargando || !input.trim()}
                className="p-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0 shadow-md shadow-blue-900/30"
              >
                {cargando
                  ? <Zap size={15} className="animate-pulse" />
                  : <Send size={15} />
                }
              </button>
            </div>
            <p className="text-center text-xs text-slate-600 mt-2">Omnia consulta la base de datos en tiempo real</p>
          </div>
        </div>

        {/* ── PANEL ALERTAS (derecha) ── */}
        <div className="hidden lg:flex flex-col w-72 xl:w-80 border-l border-slate-700/50 bg-slate-900/50 flex-shrink-0 overflow-hidden">

          {/* Header alertas */}
          <div className="px-4 py-4 border-b border-slate-700/50 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-900/40 border border-amber-800/50 flex items-center justify-center">
              <AlertTriangle size={14} className="text-amber-400" />
            </div>
            <h2 className="text-sm font-bold text-white">Centro de Alertas</h2>
            {alerts.length > 0 && (
              <span className="ml-auto text-xs font-semibold bg-red-900/60 text-red-300 border border-red-800/50 px-1.5 py-0.5 rounded-full">
                {alerts.length}
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">

            {/* Alertas configuradas */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Configuradas</p>
              <div className="space-y-1.5">
                {[
                  'Casos próximos a vencer (7 días)',
                  'Facturas vencidas',
                  'Documentos faltantes',
                  'Audiencias programadas',
                ].map(item => (
                  <div key={item} className="flex items-center gap-2 text-xs text-slate-400 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Alertas activas */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Activas</p>
              {alerts.length === 0 ? (
                <div className="text-center py-6 text-slate-600 text-xs border border-dashed border-slate-700 rounded-2xl">
                  Sin alertas activas
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.map(alert => (
                    <div key={alert.id} className="group relative overflow-hidden bg-red-950/40 border border-red-900/50 rounded-2xl p-3 hover:bg-red-950/60 transition-colors duration-150">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <AlertTriangle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-red-200 leading-relaxed font-medium">{alert.text}</p>
                        </div>
                        <button
                          onClick={() => setAlerts(prev => prev.filter(a => a.id !== alert.id))}
                          className="text-red-600 hover:text-red-300 p-0.5 rounded-md hover:bg-red-900/50 transition-colors duration-200 flex-shrink-0 opacity-0 group-hover:opacity-100"
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1.5 ml-5">{alert.time}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IAAsistente;
